/*
Vehicle Publishing Queue State Management Utilities
Provides helper functions for queue state calculations and operations
Author: Assistant
Date: 2025-06-24
*/

import {
  QueueState,
  QueueItem,
  QueueStatistics,
  QueueStatus,
  DEFAULT_QUEUE_SETTINGS,
} from '../interfaces/VehiclePublishQueue'

/**
 * Calculate queue statistics from current items
 */
export const calculateQueueStatistics = (items: QueueItem[], startTime?: Date): QueueStatistics => {
  const totalItems = items.length
  const completedCount = items.filter((item) => item.status === 'completed').length
  const failedCount = items.filter((item) => item.status === 'failed').length
  const pendingCount = items.filter((item) => item.status === 'pending').length
  const processingCount = items.filter((item) => item.status === 'processing').length
  const cancelledCount = items.filter((item) => item.status === 'cancelled').length

  // Calculate average processing time from completed items
  const completedItems = items.filter(
    (item) => item.status === 'completed' && item.timestamp.started && item.timestamp.completed
  )

  let averageProcessingTime: number | undefined

  if (completedItems.length > 0) {
    const totalProcessingTime = completedItems.reduce((sum, item) => {
      const start = item.timestamp.started!.getTime()
      const end = item.timestamp.completed!.getTime()

      return sum + (end - start)
    }, 0)
    averageProcessingTime = totalProcessingTime / completedItems.length
  }

  // Estimate time remaining
  let estimatedTimeRemaining: number | undefined

  if (averageProcessingTime && pendingCount > 0) {
    estimatedTimeRemaining = averageProcessingTime * pendingCount
  }

  return {
    totalItems,
    completedCount,
    failedCount,
    pendingCount,
    processingCount,
    cancelledCount,
    estimatedTimeRemaining,
    averageProcessingTime,
    startTime,
    endTime: completedCount + failedCount + cancelledCount === totalItems ? new Date() : undefined,
  }
}

/**
 * Determine queue status based on current state
 */
export const determineQueueStatus = (items: QueueItem[], currentStatus: QueueStatus): QueueStatus => {
  if (items.length === 0) return 'idle'

  const hasProcessing = items.some((item) => item.status === 'processing')
  const hasPending = items.some((item) => item.status === 'pending')
  const allProcessed = items.every((item) => ['completed', 'failed', 'cancelled'].includes(item.status))

  if (currentStatus === 'cancelled') return 'cancelled'

  if (currentStatus === 'paused') return 'paused'

  if (hasProcessing || hasPending) return 'processing'

  if (allProcessed) return 'completed'

  return 'idle'
}

/**
 * Calculate progress percentage (0-100)
 */
export const calculateProgress = (statistics: QueueStatistics): number => {
  if (statistics.totalItems === 0) return 0

  const processedCount = statistics.completedCount + statistics.failedCount + statistics.cancelledCount

  return Math.round((processedCount / statistics.totalItems) * 100)
}

/**
 * Generate unique queue item ID
 */
export const generateQueueItemId = (): string => {
  return `queue_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Create initial queue state
 */
export const createInitialQueueState = (): QueueState => {
  const emptyItems: QueueItem[] = []

  return {
    items: emptyItems,
    status: 'idle',
    currentIndex: -1,
    statistics: calculateQueueStatistics(emptyItems),
    settings: { ...DEFAULT_QUEUE_SETTINGS },
  }
}

/**
 * Format time duration for display
 */
export const formatDuration = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  } else {
    return `${seconds}s`
  }
}

/**
 * Get next pending item index
 */
export const getNextPendingItemIndex = (items: QueueItem[]): number => {
  return items.findIndex((item) => item.status === 'pending')
}

/**
 * Get failed items that can be retried
 */
export const getRetryableItems = (items: QueueItem[]): QueueItem[] => {
  return items.filter((item) => item.status === 'failed' && item.retryCount < item.maxRetries)
}

/**
 * Update queue item with new status and metadata
 */
export const updateQueueItem = (items: QueueItem[], itemId: string, updates: Partial<QueueItem>): QueueItem[] => {
  return items.map((item) => (item.id === itemId ? { ...item, ...updates } : item))
}

/**
 * Validate queue item data
 */
export const validateQueueItem = (item: Partial<QueueItem>): string[] => {
  const errors: string[] = []

  if (!item.vehicleId) {
    errors.push('Vehicle ID is required')
  }

  if (!item.vehicle) {
    errors.push('Vehicle data is required')
  }

  if (!item.branch) {
    errors.push('Branch data is required')
  }

  return errors
}

/**
 * Queue persistence helpers
 */
export const QUEUE_STORAGE_KEY = 'veekls_vehicle_publish_queue'

export const saveQueueToStorage = (queueState: QueueState): void => {
  if (!queueState.settings.persistQueue) return

  try {
    // Don't save function references or complex objects
    const serializable = {
      ...queueState,
      items: queueState.items.map((item) => ({
        ...item,
        timestamp: {
          queued: item.timestamp.queued.toISOString(),
          started: item.timestamp.started?.toISOString(),
          completed: item.timestamp.completed?.toISOString(),
        },
      })),
      statistics: {
        ...queueState.statistics,
        startTime: queueState.statistics.startTime?.toISOString(),
        endTime: queueState.statistics.endTime?.toISOString(),
      },
    }

    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(serializable))
  } catch (error) {
    console.warn('Failed to save queue to storage:', error)
  }
}

export const loadQueueFromStorage = (): QueueState | null => {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY)

    if (!stored) return null

    const parsed = JSON.parse(stored)

    // Convert date strings back to Date objects
    const restored: QueueState = {
      ...parsed,
      items: parsed.items.map((item: any) => ({
        ...item,
        timestamp: {
          queued: new Date(item.timestamp.queued),
          started: item.timestamp.started ? new Date(item.timestamp.started) : undefined,
          completed: item.timestamp.completed ? new Date(item.timestamp.completed) : undefined,
        },
      })),
      statistics: {
        ...parsed.statistics,
        startTime: parsed.statistics.startTime ? new Date(parsed.statistics.startTime) : undefined,
        endTime: parsed.statistics.endTime ? new Date(parsed.statistics.endTime) : undefined,
      },
    }

    return restored
  } catch (error) {
    console.warn('Failed to load queue from storage:', error)

    return null
  }
}

export const clearQueueFromStorage = (): void => {
  try {
    localStorage.removeItem(QUEUE_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear queue from storage:', error)
  }
}
